/**
 * Agentforce Chat Widget - Vanilla JS Entry Point
 * 
 * This file creates a self-contained widget that can be embedded on any website
 * with a simple script tag. It reads configuration from data attributes and
 * initializes the React component.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import '../index.css';
import AgentforceChat from '../components/AgentforceChat';

// Widget configuration interface
interface WidgetConfig {
  orgId: string;
  companyName?: string;
  title?: string;
  welcomeMessage?: string;
  placeholderText?: string;
  widgetDomain?: string;
  primaryColor?: string;
  backgroundColor?: string;
  bubbleColor?: string;
  iconImage?: string; // Chat button icon
  headerImage?: string; // Header icon
  apiEndpoint?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
}

// Default configuration
const DEFAULT_CONFIG: Partial<WidgetConfig> = {
  companyName: 'Support',
  title: 'AI Assistant',
  welcomeMessage: 'Hi! How can I help you today?',
  placeholderText: 'Type your message...',
  widgetDomain: 'https://opptymizer.com',
  primaryColor: '#00a0e0',
  backgroundColor: '#ffffff',
  bubbleColor: '#f8fafc',
  apiEndpoint: 'https://www.opptymizer.com/api/chat',
  position: 'bottom-right'
};

/**
 * Reads configuration from the script tag data attributes
 */
function getConfigFromScript(): WidgetConfig | null {
  // Find the script tag that loaded this widget
  const scripts = document.querySelectorAll('script[src*="agentforce-chat"]');
  const scriptTag = scripts[scripts.length - 1] as HTMLScriptElement;
  
  if (!scriptTag) {
    console.error('AgentforceChat: Could not find script tag');
    return null;
  }

  // Auto-detect widget domain from script source if not provided
  let detectedDomain = DEFAULT_CONFIG.widgetDomain;
  if (scriptTag.src) {
    try {
      const url = new URL(scriptTag.src);
      detectedDomain = `${url.protocol}//${url.host}`;
      console.log('AgentforceChat: Auto-detected widget domain:', detectedDomain);
    } catch (e) {
      console.warn('AgentforceChat: Could not parse script URL, using default domain');
    }
  }

  // Extract data attributes
  const dataset = scriptTag.dataset;
  
  // Validate required fields
  if (!dataset.orgId) {
    console.error('AgentforceChat: data-org-id is required');
    return null;
  }

  // Build configuration object
  const config: WidgetConfig = {
    orgId: dataset.orgId,
    companyName: dataset.companyName || DEFAULT_CONFIG.companyName,
    title: dataset.title || DEFAULT_CONFIG.title,
    welcomeMessage: dataset.welcomeMessage || DEFAULT_CONFIG.welcomeMessage,
    placeholderText: dataset.placeholderText || DEFAULT_CONFIG.placeholderText,
    widgetDomain: dataset.widgetDomain || detectedDomain,
    primaryColor: dataset.primaryColor || DEFAULT_CONFIG.primaryColor,
    backgroundColor: dataset.backgroundColor || DEFAULT_CONFIG.backgroundColor,
    bubbleColor: dataset.bubbleColor || DEFAULT_CONFIG.bubbleColor,
    iconImage: dataset.iconImage, // Chat button icon
    headerImage: dataset.headerImage, // Header icon
    apiEndpoint: dataset.apiEndpoint || DEFAULT_CONFIG.apiEndpoint,
    position: (dataset.position as any) || DEFAULT_CONFIG.position
  };

  return config;
}

/**
 * Creates a container element with Shadow DOM for complete CSS isolation
 */
function createWidgetContainer(): { container: HTMLElement, shadowRoot: ShadowRoot, reactRoot: HTMLElement } {
  const container = document.createElement('div');
  container.id = 'agentforce-chat-widget';
  container.style.cssText = `
    position: fixed;
    z-index: 999;
    pointer-events: auto;
  `;
  
  // Create Shadow DOM for complete CSS isolation
  const shadowRoot = container.attachShadow({ mode: 'closed' });
  
  // Create React mount point inside shadow
  const reactRoot = document.createElement('div');
  reactRoot.id = 'agentforce-react-root';
  shadowRoot.appendChild(reactRoot);
  
  document.body.appendChild(container);
  return { container, shadowRoot, reactRoot };
}

/**
 * Applies custom CSS variables for theming inside Shadow DOM
 */
function applyCustomStyles(shadowRoot: ShadowRoot, config: WidgetConfig): void {
  const style = document.createElement('style');
  style.textContent = `
    :host {
      --agentforce-primary-color: ${config.primaryColor};
      --agentforce-background-color: ${config.backgroundColor};
      --agentforce-bubble-color: ${config.bubbleColor};
    }
  `;
  shadowRoot.appendChild(style);
}

/**
 * Load widget CSS into Shadow DOM
 */
function loadWidgetCSS(shadowRoot: ShadowRoot): void {
  // Check if CSS is already loaded in this shadow root
  if (shadowRoot.querySelector('link[href*="agentforce-chat"]') || shadowRoot.querySelector('style[data-agentforce-css]')) {
    return;
  }

  const cssUrl = 'https://www.opptymizer.com/agentforce/style.css';
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = cssUrl;
  link.onload = () => console.log('✅ AgentforceChat CSS loaded successfully into Shadow DOM');
  link.onerror = () => console.warn('⚠️ Could not load AgentforceChat CSS from', cssUrl);
  shadowRoot.appendChild(link);
}

/**
 * Initializes the Agentforce Chat Widget
 */
function initializeWidget(): void {
  // Prevent multiple initializations
  if (window.AgentforceChatInitialized) {
    console.warn('AgentforceChat: Widget already initialized');
    return;
  }

  // Get configuration from script tag
  const config = getConfigFromScript();
  if (!config) {
    return;
  }

  console.log('🚀 Initializing AgentforceChat Widget:', config);

  // Create container with Shadow DOM
  const { container, shadowRoot, reactRoot } = createWidgetContainer();

  // Load widget CSS into Shadow DOM
  loadWidgetCSS(shadowRoot);

  // Apply custom styles to Shadow DOM
  applyCustomStyles(shadowRoot, config);

  // Create React root and render component inside Shadow DOM
  const root = createRoot(reactRoot);
  
  root.render(
    React.createElement(AgentforceChat, {
      orgId: config.orgId,
      companyName: config.companyName,
      welcomeMessage: config.welcomeMessage,
      placeholderText: config.placeholderText,
      widgetDomain: config.widgetDomain,
      primaryColor: config.primaryColor,
      backgroundColor: config.backgroundColor,
      bubbleColor: config.bubbleColor,
      iconImage: config.iconImage, // Chat button icon
      headerImage: config.headerImage, // Header icon
      position: config.position,
      apiEndpoint: config.apiEndpoint,
      // Add callbacks for debugging
      onSessionStart: (sessionId: string) => {
        console.log('AgentforceChat: Session started:', sessionId);
      },
      onMessageSent: (message: string) => {
        console.log('AgentforceChat: Message sent:', message);
      },
      onError: (error: string) => {
        console.error('AgentforceChat: Error:', error);
      }
    })
  );

  // Mark as initialized
  window.AgentforceChatInitialized = true;
  
  console.log('✅ AgentforceChat Widget initialized successfully');
}

/**
 * Auto-initialize when DOM is ready
 */
function autoInitialize(): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWidget);
  } else {
    // DOM is already loaded
    initializeWidget();
  }
}

// Global type declarations
declare global {
  interface Window {
    AgentforceChatInitialized?: boolean;
    AgentforceChat?: {
      init: () => void;
    };
  }
}

// Expose global API for manual initialization
window.AgentforceChat = {
  init: initializeWidget
};

// Auto-initialize by default
autoInitialize();

// Export for module systems (if needed)
export default {
  init: initializeWidget
};
